/* inferIsMeta — pure, fail-loud IS chart-of-accounts classifier for onboarding Step 3.
 *
 * Generalizes the one-shot `server/seed-roles-{semitek,chris-test,ae-model}.js`
 * scripts (which hand-set `role` per tenant) into a tenant-agnostic inference, so a
 * NEW tenant exits Step 3 with a fully classified `gl_line_mappings` instead of the
 * bare 7 columns the save endpoint used to write.
 *
 * ★ WHY THIS EXISTS — the gap it closes is the worst in the system (§12.5 B3-1):
 *   `buildISLines.js` gates EVERY IS leaf on `.filter(r => r.statement_type === 'IS'
 *   && r.role)`. With `role` NULL, glLeaves is EMPTY — the income statement has no GL
 *   leaf lines at all — and `computeVendor.js` (`if (!isLines[v.isCat]) return;`)
 *   then silently drops EVERY vendor's spend to $0. The model still computes and the
 *   balance sheet still balances. The numbers are simply wrong.
 *
 * SHAPE mirrors `inferBsMeta.js`:
 *   - PURE: no I/O, no DB, no hardcoded tenant line-ids.
 *   - KEYWORD-DRIVEN: keys on (is_category, gl_name, gl_category) — the same signals
 *     the existing Step-3 suggesters already use.
 *   - ★ FAIL-LOUD: throws on an unclassifiable line. It NEVER silently defaults —
 *     a wrong-but-plausible role mis-routes an engine (role selects `source` via
 *     ROLE_TO_SOURCE), which is the exact class of bug this module prevents.
 *
 * DIVISION OF LABOUR — infer() proposes, the operator disposes:
 *   - `role` + `subsection` are INFERRED here (reproduction-gated against all three
 *     live tenants) and shown read-only in the Step-3 grid.
 *   - `sort_order` is NOT inferred — it is COPIED from historical_gl_lines.sort_order
 *     (the source workbook's own row order) and then banded payroll-first.
 *   - `display_group` is the one field needing operator intent: merging Sales +
 *     Marketing into one `sales_mktg` sub-section is a judgement no rule can make.
 *     inferDisplayGroups() proposes; the operator overrides in the grid.
 */

import { PAYTYPE_SORT_OFFSET, PAYROLL_ROLE_TO_PAYTYPE, DG_BAND_WIDTH, buildDgSortBases, dgSortBase } from '../engines/payrollKeys';
// ★ B4 — THE ONE department-name slugifier. runFullModel keys payrollHC with it, the
// per-seat resolver builds its option ids from it, AdminPage writes department_mappings
// with it, and KPIDashboardPage buckets on it. This file used to carry a near-copy.
import { deptSlug, groupIdOf } from './seatDepartments';

/* is_category (display form, as stored by Step 3) → subsection slug.
 * 'Other' is deliberately absent: it splits into ooe (D&A) vs oie (everything
 * else) on the role, not the category — the Path-1 D&A architecture. */
export const IS_CAT_TO_SUBSECTION = {
  'COGS': 'cogs',
  'S&M':  'sm',
  'G&A':  'ga',
  'R&D':  'rd',
};

const EXPENSE_CATS = new Set(Object.keys(IS_CAT_TO_SUBSECTION));

export const PAYROLL_ROLES = new Set([
  'payroll_wages', 'payroll_bonuses', 'payroll_commissions',
  'payroll_tax', 'payroll_benefits',
]);

/* Payroll role from the GL line's own name. ★ Checked BEFORE is_category, and
 * ORDER MATTERS inside it:
 *   - "Payroll Taxes" must beat the /tax/ rule that would otherwise reach it.
 *   - "Sales Compensation" / "G&A Compensation" are WAGES — the operator's label for
 *     the salary line, not a separate concept (AE Model uses both; Semitek's
 *     sm_compensation-vs-sm_wages asymmetry is the same thing).
 * Returns null when the line is not payroll — the caller then treats it as a vendor
 * line, which is correct: "Payroll / HR" (a payroll-SOFTWARE vendor) and "Recruiting"
 * must NOT match here. */
/* Normalize a GL label for matching. Underscores/hyphens become spaces so a
 * slug-shaped name ("cogs_payroll_tax", typed by an operator or seeded by a script)
 * matches the same rules as a human one ("Payroll Taxes"). Underscore is a \w
 * character, so without this "\bwages\b" does NOT match inside "cogs_wages". */
const norm = s => (s || '').toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

export function matchPayrollRole(glName) {
  const l = norm(glName);
  if (/payroll\s*tax|\bfica\b|\bfuta\b|\bsuta\b|unemployment|employer\s*tax/.test(l)) return 'payroll_tax';
  if (/benefit|\b401k\b|health\s*insurance|medical|dental|vision/.test(l))            return 'payroll_benefits';
  if (/commission/.test(l))                                                            return 'payroll_commissions';
  if (/^bonus(es)?$/.test(l) || /\bbonus(es)?\b/.test(l))                              return 'payroll_bonuses';
  if (/salar|\bwages?\b|compensation/.test(l))                                         return 'payroll_wages';
  return null;
}

/* Below-the-line (is_category='Other') role + subsection.
 * ORDER MATTERS: "Interest Income" must hit the interest rule before the
 * trailing-"Income" rule, or it would book as other income and lose its sweep
 * wiring. D&A goes to `ooe`; everything else to `oie`. */
function matchOtherRole(glName) {
  const l = norm(glName);
  if (/depreciation/.test(l)) return { role: 'depreciation',  subsection: 'ooe' };
  if (/amortization/.test(l)) return { role: 'amortization',  subsection: 'ooe' };
  if (/interest/.test(l))     return { role: 'oi_interest',   subsection: 'oie' };
  if (/\btax(es)?\b/.test(l)) return { role: 'oi_taxes',      subsection: 'oie' };
  if (/income\s*$/.test(l))   return { role: 'oi_other_inc',  subsection: 'oie' };
  return { role: 'oi_other_exp', subsection: 'oie' };
}

/* Classify ONE IS gl_line_mappings row. `row` = {gl_name, gl_category, is_category}.
 * Returns { role, subsection }. Throws when it cannot classify — never guesses. */
export function inferIsMetaLeaf(row) {
  const isCat = row.is_category;
  const glName = row.gl_name || '';

  if (isCat === 'Revenue') {
    return { role: 'revenue_line', subsection: 'revenue' };
  }

  if (EXPENSE_CATS.has(isCat)) {
    const sub = IS_CAT_TO_SUBSECTION[isCat];
    const pr = matchPayrollRole(glName);
    // Not payroll ⇒ a vendor/opex line. This is a POSITIVE classification, not a
    // fallback: every non-payroll line in an expense category is spend, and
    // `vendor_line` is what routes it (ROLE_TO_SOURCE → source:'vendor').
    return { role: pr || 'vendor_line', subsection: sub };
  }

  if (isCat === 'Other') {
    return matchOtherRole(glName);
  }

  throw new Error(
    `[inferIsMeta] Cannot classify "${glName}": unknown IS category "${isCat}". ` +
    `Expected one of Revenue, ${[...EXPENSE_CATS].join(', ')}, Other. ` +
    `REFUSING to guess — a NULL role drops the line off the income statement entirely ` +
    `and silently zeroes every vendor mapped to it.`
  );
}

/* -- the department token-match ---------------------------------------------------------
 * `tokens` below serves resolvePayrollDept, the Step-5c payroll history back-fill's matcher.
 * inferDisplayGroupForGlCategory, which used the same rule to FILL a blank display_group on a
 * GL row at Step 3, was DELETED 2026-09-28 (W-022, Brian's decision): a blank grouping stays
 * blank. It was a guess shown as a decision, and a GL line's grouping is display-only --
 * measured, a blank, the guess or a wrong group moves 0 leaf / total / NI / BS / CFS cells.
 */
const tokens = s => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean);

/* ★ B4 — the local `slugifyDept` that stood here is DELETED; this file now uses the ONE
   department-name slugifier, `deptSlug` (utils/seatDepartments.js), imported at the top.
   The two were character-identical except that deptSlug ends `|| "dept"`, so a name with
   no alphanumerics at all ("---", "") slugged to "" here and to "dept" there. deptSlug is
   the safer of the two: an EMPTY display_group composes a malformed payroll IS-line key
   (`sm__wages`), whereas "dept" is at least well-formed and visible.
   Measured inert: all 17 live department identifiers slug identically, and in fact the
   function is UNREACHABLE on current data -- the call site below (resolvePayrollDept) reads
   `dm.display_group || slug(...)` and every live department row has a non-null
   display_group, so the || short-circuits. Proven equivalent by a synthetic that forces
   the call (see diffs/b4-build.txt). */

/* Build C (C-10): resolve a payroll row's gl_category (which is a DEPARTMENT NAME, e.g.
 * "Client Success, Support & Imp") to its { display_group, is_category } via the SAME token-subset
 * rule the retired Step-3 display_group guess and the removed server back-fill arm used -- a department matches when its name's
 * tokens are all present in the gl_category's tokens. Returns BOTH fields (the caller needs
 * is_category for cat_slug — a Skip'd payroll row's own is_category is 'Skip', unusable) plus the
 * matched departments and ambiguity flags so the caller can FAIL LOUD (never guess a dg/cat).
 * This is NOT a new matcher — it exposes the resolution the token rule already performs. */
export function resolvePayrollDept(glCategory, deptMappings) {
  const cat = new Set(tokens(glCategory));
  if (!cat.size) return { matched: [], display_group: null, is_category: null, dgAmbiguous: false, catAmbiguous: false };
  const matched = (deptMappings || []).filter(dm => {
    const dept = tokens(dm.department_name);
    return dept.length && dept.every(t => cat.has(t));
  });
  const dgs = new Set(matched.map(dm => dm.display_group || deptSlug(dm.department_name)));
  const cats = new Set(matched.map(dm => dm.is_category).filter(Boolean));
  return {
    matched: matched.map(dm => dm.department_name),
    display_group: dgs.size === 1 ? [...dgs][0] : null,
    is_category: cats.size === 1 ? [...cats][0] : null,
    dgAmbiguous: dgs.size > 1,
    catAmbiguous: cats.size > 1,
  };
}

/* ── sort_order ──────────────────────────────────────────────────────────────
 * NOT inferred — the default is the tenant's OWN workbook row order, already
 * persisted at Step-2 import as historical_gl_lines.sort_order. This function only
 * BANDS it: payroll leaves first, then everything else in workbook order, inside the
 * display_group's band (see payrollKeys.js).
 *
 * DECISION (operator, locked): payroll-first in EVERY subsection — including COGS,
 * where the workbook itself lists Merchant Fee / ISP Fees ahead of Salaries. The
 * normalized statement wins over the source file's local quirk.
 *
 * `leaves` = [{ key, gl_name, gl_category, is_category, role, subsection,
 *               display_group, src_sort }] in ANY order; src_sort is the
 *               historical_gl_lines.sort_order (workbook row index).
 * Returns { [key]: sort_order }.
 */
export function buildIsSortOrders(leaves, deptMappings) {
  const bases = buildDgSortBases(deptMappings, IS_CAT_TO_SUBSECTION);
  const bucket = new Map();               // `${subsection}|${dg}` → leaves
  for (const l of leaves) {
    const k = `${l.subsection}|${groupIdOf(l.display_group) || ''}`;   // 2026-10-28: a template group TEXT is its slug
    if (!bucket.has(k)) bucket.set(k, []);
    bucket.get(k).push(l);
  }

  const out = {};
  for (const [, group] of bucket) {
    // Workbook order is the tiebreaker for everything.
    const ordered = [...group].sort((a, b) => (a.src_sort ?? 999999) - (b.src_sort ?? 999999));
    const base = dgSortBase(bases, ordered[0].subsection, groupIdOf(ordered[0].display_group));
    let n = 0;
    for (const l of ordered) {
      if (PAYROLL_ROLES.has(l.role)) {
        /* ★★ THE role → paytype TRANSLATION NOW COMES FROM payrollKeys, NOT FROM HERE.
         *
         * This was an inline ternary chain, and AdminPage carried the same translation
         * as an object literal — two statements of one rule, agreeing today, which is
         * exactly the state §6 names as guaranteed future drift. Both now read the ONE
         * exported map, which lives beside PAYTYPES and PAYTYPE_SORT_OFFSET so it cannot
         * disagree with the vocabulary it is keyed into.
         *
         * ★ AND THE OLD CHAIN WAS RIGHT BY ACCIDENT. Its trailing `: 'commissions'` was a
         * FALL-THROUGH, not a case: it answered "commissions" for ANY role it did not
         * name. It agreed with the map on all five real roles only because the fifth one
         * happens to BE payroll_commissions. A sixth role would have been silently filed
         * under commissions, in the right-looking band, forever.
         *
         * ★ SO THE LOOKUP FAILS LOUD INSTEAD. A bare lookup would yield undefined and
         * `base + PAYTYPE_SORT_OFFSET[undefined]` is NaN — a sort_order that is silently
         * corrupt rather than visibly wrong, which is the worse of the two failures. The
         * PAYROLL_ROLES guard one line up makes this throw UNREACHABLE on any current
         * data, and that is the point: it costs nothing today and converts a future
         * unknown role from a silent NaN into a named stop. */
        const paytype = PAYROLL_ROLE_TO_PAYTYPE[l.role];
        if (!paytype) {
          throw new Error(
            `[inferIsMeta] Cannot band payroll leaf "${l.key}": role "${l.role}" has no ` +
            `paytype in PAYROLL_ROLE_TO_PAYTYPE. Expected one of ` +
            `${Object.keys(PAYROLL_ROLE_TO_PAYTYPE).join(", ")}. ` +
            `REFUSING to guess — a missing paytype yields NaN for this leaf's sort_order, ` +
            `which re-orders the income statement with no error.`
          );
        }
        out[l.key] = base + PAYTYPE_SORT_OFFSET[paytype];
      } else {
        const order = base + 50 + n;
        n += 1;
        // ★ Fail loud rather than silently bleed into the next group's band — an
        // overflowing band would re-order the income statement with no error.
        if (order >= base + DG_BAND_WIDTH) {
          throw new Error(
            `[inferIsMeta] sort band ${base} overflowed (${n} non-payroll leaves in ` +
            `subsection="${l.subsection}" display_group="${l.display_group || '—'}"). Widen DG_BAND_WIDTH.`
          );
        }
        out[l.key] = order;
      }
    }
  }
  return out;
}

/* ── Part B — RE-BASE STALE FALLBACK BANDS ────────────────────────────────────
 *
 * THE DEFECT THIS CLOSES. Step 3 (GL mapping) runs BEFORE Step 5 (departments).
 * With no department_mappings yet, dgSortBase returns its FIRST-BAND FALLBACK
 * (DG_BAND_WIDTH), so buildIsSortOrders above writes `1000 + 50 + n` for every
 * leaf. That is CORRECT while the subsection has one band — the fallback's own
 * comment says only relative order matters there, and it is right. It becomes
 * WRONG the moment Step 5c creates a SECOND display_group in that subsection and
 * the real bands (1000, 2000, 3000, …) start separating groups: a leaf still
 * carrying the fallback band now sits inside a DIFFERENT group's band.
 * `sort_order` is INSERT-only (it may carry an operator ▲▼ reorder), so nothing
 * ever revised it. Measured on cid=8: six sales_mktg vendor leaves stuck at
 * 1050-1055 while sales_mktg's real band was 4000, which BOTH moved the group
 * from 4th to 2nd (group order is min(sort_order)) AND sorted those vendor lines
 * above their own group's payroll lines (1050 < 4010).
 *
 * ★ RE-BANDING IS NOT RE-ORDERING. The insert-only rule protects the operator's
 * RELATIVE ORDER, not the absolute integer. This takes a group's leaves in their
 * CURRENT sorted order and re-bases them onto the correct band, so an operator
 * reorder survives intact and a second run is a no-op.
 *
 * ★ THREE THINGS IT DELIBERATELY WILL NOT DO — each one measured, not assumed:
 *   1. SINGLE-GROUP SUBSECTIONS ARE NEVER TOUCHED. Their leaves legitimately sit
 *      at the fallback band (or below it — cid=3's cogs/ga/rd sit at 10-60). A
 *      naive "is it in its band" test over every leaf produced 35 FALSE POSITIVES
 *      on cid=3. Bands only separate anything when a subsection has ≥2 groups.
 *   2. A LEAF WHOSE display_group DOES NOT RESOLVE TO A REAL DEPARTMENT BAND IS
 *      FLAGGED, NEVER MOVED. This runs unattended on every future tenant; an
 *      unexpected display_group would otherwise be re-based into the wrong band
 *      confidently and silently. Verify before acting.
 *   3. PAYROLL-ROLED LEAVES ARE FLAGGED, NEVER MOVED. Their position interacts
 *      with dynPayrollLeaves (generated at base + PAYTYPE_SORT_OFFSET and not
 *      stored), and no live tenant has a payroll-roled glm row inside a
 *      multi-group subsection — so moving them would be an unexercised path
 *      taken unattended. Out of scope by choice, surfaced so it cannot hide.
 *
 * PURE — computes a plan, writes nothing. THE WALL: the caller persists via the
 * existing transactional reorder endpoint (client computes, server persists).
 *
 * @param glMappings  gl_line_mappings rows (needs id, statement_type, role,
 *                    subsection, display_group, sort_order, is_line_key)
 * @param deptMappings department_mappings rows (is_category, display_group,
 *                    sort_order, id)
 * @returns { lines, skipped, groups }
 *          lines[]   = { id, sort_order, is_line_key, from, to } — ONLY rows whose
 *                      value actually changes; empty ⇒ nothing to do (idempotent)
 *          skipped[] = { id, is_line_key, subsection, display_group, reason }
 *          groups[]  = { subsection, display_group, base, members } — diagnostics
 */
export function planSortOrderReband(glMappings, deptMappings) {
  const bases = buildDgSortBases(deptMappings, IS_CAT_TO_SUBSECTION);
  const lines = [];
  const skipped = [];
  const groups = [];

  const isLeaves = (glMappings || []).filter(m => m.statement_type === 'IS' && m.role);

  for (const catSlug of Object.keys(bases)) {
    const forCat = bases[catSlug];
    const dgs = Object.keys(forCat);
    // (1) single-group subsection — the fallback band IS this group's band.
    if (dgs.length < 2) continue;

    const inSub = isLeaves.filter(m => m.subsection === catSlug);

    // (2) THE display_group GUARD — resolve BEFORE acting, flag what does not.
    for (const l of inSub) {
      const dg = groupIdOf(l.display_group) || catSlug;   // 2026-10-28: a template group TEXT is its slug
      if (forCat[dg] == null) {
        skipped.push({
          id: l.id, is_line_key: l.is_line_key, subsection: catSlug,
          display_group: l.display_group ?? null,
          reason: 'display_group does not resolve to a department band — not moved',
        });
      }
    }

    for (const dg of dgs) {
      const base = forCat[dg];
      const members = inSub.filter(l => (groupIdOf(l.display_group) || catSlug) === dg);
      if (!members.length) continue;

      const inBand = so => Number(so) >= base && Number(so) < base + DG_BAND_WIDTH;

      // (3) payroll-roled leaves: flag out-of-band, never move.
      for (const l of members) {
        if (PAYROLL_ROLES.has(l.role) && !inBand(l.sort_order)) {
          skipped.push({
            id: l.id, is_line_key: l.is_line_key, subsection: catSlug, display_group: dg,
            reason: 'payroll-roled leaf is out of band — flagged, not moved',
          });
        }
      }

      const movable = members.filter(l => !PAYROLL_ROLES.has(l.role));
      const settled = movable.length > 0 && movable.every(l => inBand(l.sort_order));
      // Diagnostics record every group CONSIDERED, not only those re-based — so a
      // log or a test can answer "did it look at this group and find nothing?"
      // rather than having to infer it from an absence.
      groups.push({
        subsection: catSlug, display_group: dg, base,
        movable: movable.length, alreadyInBand: settled,
      });
      if (!movable.length) continue;
      // IDEMPOTENCE: whole group already in band ⇒ nothing to plan.
      if (settled) continue;

      // Re-base the group as ONE ordered list so a group split across two bands
      // cannot interleave. Sort by current sort_order (id breaks ties
      // deterministically — a derivation must not depend on row arrival order).
      const ordered = [...movable].sort((a, b) =>
        (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || (a.id ?? 0) - (b.id ?? 0));

      ordered.forEach((l, n) => {
        const next = base + 50 + n;
        if (next >= base + DG_BAND_WIDTH) {
          skipped.push({
            id: l.id, is_line_key: l.is_line_key, subsection: catSlug, display_group: dg,
            reason: `band ${base} would overflow at ${next} — not moved (widen DG_BAND_WIDTH)`,
          });
          return;
        }
        if (Number(l.sort_order) !== next) {
          lines.push({
            id: l.id, is_line_key: l.is_line_key,
            sort_order: next, from: Number(l.sort_order), to: next,
          });
        }
      });
    }
  }

  return { lines, skipped, groups };
}
